import { ArrowRight } from 'lucide-react'
import * as React from 'react'

import { DiffErrorBoundary } from '@/components/DiffViewer/DiffErrorBoundary'
import { diffStats } from '@/components/DiffViewer/diffStats'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'

// The boundary is what the tests mock; the build emits one chunk either way.
const DiffViewer = React.lazy(() =>
  import('@/components/DiffViewer').then((m) => ({ default: m.DiffViewer }))
)

export type DiffMode = 'unified' | 'split'

export const SEGMENTED = 'rounded-md bg-neutral-hover-bg p-0.5'
export const SEGMENT =
  'h-6 px-2 text-xs text-muted-foreground hover:bg-transparent data-[state=on]:bg-card data-[state=on]:text-foreground data-[state=on]:shadow-sm'

/** `old` against `new`, named "from → to", with its +/- line counts. */
export function CompareView({
  from,
  to,
  oldValue,
  newValue,
  mode,
  onModeChange,
  action,
}: {
  from: string
  to: string
  oldValue: string
  newValue: string
  mode: DiffMode
  /** Omitted where split does not fit: the toggle hides and unified shows. */
  onModeChange?: (mode: DiffMode) => void
  action?: React.ReactNode
}) {
  const { added, removed } = diffStats(oldValue, newValue)
  const same = oldValue === newValue

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
        <span className="flex min-w-0 items-center gap-1.5 font-medium text-primary-high-contrast">
          <span className="truncate">{from}</span>
          <ArrowRight
            role="img"
            aria-label="to"
            className="size-3.5 shrink-0"
          />
          <span className="truncate">{to}</span>
        </span>
        {!same && (
          <span className="font-mono">
            <span className="text-success-solid">{`+${added}`}</span>{' '}
            <span className="text-destructive">{`-${removed}`}</span>
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {onModeChange && !same && (
            <ToggleGroup
              type="single"
              aria-label="Diff layout"
              value={mode}
              onValueChange={(next) => next && onModeChange(next as DiffMode)}
              className={SEGMENTED}>
              <ToggleGroupItem value="unified" className={SEGMENT}>
                Unified
              </ToggleGroupItem>
              <ToggleGroupItem value="split" className={SEGMENT}>
                Split
              </ToggleGroupItem>
            </ToggleGroup>
          )}
          {action}
        </span>
      </div>
      {same ? (
        <p className="py-6 text-center text-sm text-neutral-low-contrast">
          No differences
        </p>
      ) : (
        <DiffErrorBoundary
          fallback={
            <div className="py-10 text-center text-sm text-neutral-low-contrast">
              Couldn’t load the diff viewer. Reload to try again.
            </div>
          }>
          <React.Suspense fallback={<Skeleton className="h-32 w-full" />}>
            <DiffViewer
              oldValue={oldValue}
              newValue={newValue}
              // Any name without a known extension makes the library warn on
              // every render, though highlighting is off.
              fileName="snippet.txt"
              mode={onModeChange ? mode : 'unified'}
            />
          </React.Suspense>
        </DiffErrorBoundary>
      )}
    </div>
  )
}
